/* OEM storefront quote client — window.OEMPricing.
   All pricing/eligibility decisions are made server-side (vindecoder,
   utils/oem_pricing.py) and reached through the "vinproxy" Shopify app
   proxy: requests to /apps/vin/<VIN> stay on this domain, Shopify forwards
   and HMAC-signs them, and the backend rejects anything unsigned. The
   response carries decisions only (which variant per product) — prices are
   displayed from Shopify-rendered page data keyed by those variants. */
(function () {
  'use strict';

  var QUOTE_API = '/apps/vin/';

  // Additional storefront guard; never adds an option the server did not offer.
  function canOfferP575(vehicle) {
    var engine = String(vehicle.engine || '');
    var code = engine.match(/\b([PD]\d{3}e?)\b/i);
    if (code) return /^(P500|P525)$/i.test(code[1]);
    if (/diesel|hybrid|phev/i.test(engine)) return false;
    var power = String(vehicle.power_output || '').match(/^(?:\s*)(\d+)(?:\s*PS)?\s*$/i);
    if (!power) return false;
    var ps = Number(power[1]);
    // Eligibility is stricter than the tolerant display-spec lookup.
    return ps === 500 || ps === 525;
  }

  // Fetch the curated quote, shaped with the legacy /api/vin field names the
  // render code consumes. Decisions ride on __quote:
  //   packages: {handle: {variant_id, variant_title}} — a non-"Standard"
  //   title is a discount
  //   power:    {variant_id, variant_title} | null
  // feature_ids carries only the server-intersected sellable activation ids.
  function fetchQuote(vin) {
    return fetch(QUOTE_API + encodeURIComponent(vin)).then(function (r) {
      return r.json().catch(function () { return null; }).then(function (q) {
        if (r.ok && q && q.status === 'success' && q.vehicle && q.vehicle.model) return q;
        var code = q && (q.error_code || q.status);
        // Support the old backend during rollout, but never infer a missing
        // VIN from an empty response, proxy error, or missing vehicle model.
        if (code === 'VIN not found!' || code === 'VIN not found') code = 'not_found';
        if (code === 'VIN is invalid') code = 'invalid_vin';
        if (code === 'Multiple vehicles match this short VIN; full VIN required') code = 'ambiguous_vin';
        if (r.status >= 500) code = 'service_unavailable';
        if (r.status === 429) code = 'rate_limited';
        var messages = {
          not_found: 'VIN not found. Please check it and try again.',
          invalid_vin: 'Please enter a valid VIN.',
          ambiguous_vin: 'Multiple vehicles match. Please enter the full 17-character VIN.',
          rate_limited: 'Too many lookups. Please wait a moment and try again.',
          service_unavailable: 'Vehicle lookup is temporarily unavailable. Please try again shortly.'
        };
        if (!Object.prototype.hasOwnProperty.call(messages, code)) code = 'service_unavailable';
        var error = new Error(messages[code]);
        error.code = code;
        error.httpStatus = r.status;
        throw error;
      });
    }).then(function (q) {
      var v = q.vehicle || {};
      if (!canOfferP575(v)) {
        var isPremium = function (option) { return /\bP575\b/i.test(option.variant_title || ''); };
        if (Array.isArray(q.power_options)) q.power_options = q.power_options.filter(function (option) { return !isPremium(option); });
        if (q.power && isPremium(q.power)) q.power = (q.power_options || [])[0] || null;
        if (!q.power && !(q.power_options || []).length) q.has_power_upgrade = false;
      }
      return {
        vin: q.vin,
        model: v.model,
        model_year: v.model_year,
        country: v.country,
        engine: v.engine,
        power_output: v.power_output,
        trim_level: v.trim_level,
        feature_ids: q.activation_ids || [],
        has_power_upgrade: q.has_power_upgrade === true,
        status: 'success',
        __quote: q
      };
    }).finally(function () {
      window.dispatchEvent(new Event('oem:vin-lookup-complete'));
    });
  }

  window.OEMPricing = {
    fetchQuote: fetchQuote,
    canOfferP575: canOfferP575
  };
})();
